import {VisuallyHidden} from '@radix-ui/react-visually-hidden';
import cn from 'classnames';
import React from 'react';

import Icon from '@/components/Icon/Icon';
import appContent from '@/services/content/appContent.typed';
import type {AutoDeliveryBase, AutoDeliveryOrder} from '@/types/icc/autoDeliveryDetails';
import {twx} from '@/utils/tailwindUtils';

import type {VisualState} from './AutoDeliveryDcxDeliveryStepper.helpers';
import {getDeliveryStepperRenderModel} from './AutoDeliveryDcxDeliveryStepper.helpers';

type AutoDeliveryDcxDeliveryStepperProps = {
    className?: string;
    status?: AutoDeliveryBase['status'];
    orderStatus?: AutoDeliveryOrder['status'];
    statusUpdateReason?: AutoDeliveryBase['statusUpdateReason'];
};

const renderStepContent = (state: VisualState) => {
    if (state !== 'done') return null;

    return (
        <Icon width='8' height='7' fill='none'>
            <path
                d='M6.98586 1L2.90372 5.50393L0.999932 3.75151'
                stroke='#FFFDFB'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
            />
        </Icon>
    );
};

const AutoDeliveryDcxDeliveryStepper = ({
    className,
    status,
    orderStatus,
    statusUpdateReason,
}: AutoDeliveryDcxDeliveryStepperProps) => {
    const stepperModel = getDeliveryStepperRenderModel({status, orderStatus, statusUpdateReason});

    if (!stepperModel) return null;

    const {
        createdDeliveryProgressStatusLabel,
        shippedDeliveryProgressStatusLabel,
        completedDeliveryProgressStatusLabel,
    } = appContent.getAutoDeliveryContent() || {};

    const stepLabels = [
        createdDeliveryProgressStatusLabel,
        shippedDeliveryProgressStatusLabel,
        completedDeliveryProgressStatusLabel,
    ];
    const {steps, activeStepIndex} = stepperModel;
    const activeStepLabel = stepLabels[activeStepIndex] || '';

    return (
        <div className={twx('text-12 tracking-02', className)}>
            <div className='flex items-center gap-2' aria-hidden='true'>
                {steps.map(({step, connector}, stepIndex) => {
                    return (
                        <React.Fragment key={stepIndex}>
                            <span
                                className={cn(
                                    'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-[3px]',
                                    {
                                        done: 'border-signal-success bg-signal-success',
                                        active: 'border-signal-success',
                                        default: 'border-primary-tins-slate',
                                        error: 'border-bonds-red',
                                    }[step]
                                )}
                            >
                                {renderStepContent(step)}
                            </span>
                            {connector && (
                                <span
                                    className={cn(
                                        'h-[3px] w-full rounded-full',
                                        {
                                            done: 'bg-signal-success',
                                            default: 'bg-primary-tins-slate',
                                            error: 'bg-bonds-red',
                                        }[connector]
                                    )}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
            <div className='flex justify-between text-center leading-[20px] text-scarpa-flow'>
                {stepLabels.map((label, stepIndex) => (
                    <span
                        className={cn('flex-1 truncate', {
                            'text-start': stepIndex === 0,
                            'text-end': stepIndex === stepLabels.length - 1,
                        })}
                        key={stepIndex}
                    >
                        {stepIndex === activeStepIndex && <VisuallyHidden>{activeStepLabel}</VisuallyHidden>}
                        <span aria-hidden='true'>{label}</span>
                    </span>
                ))}
            </div>
        </div>
    );
};

export default AutoDeliveryDcxDeliveryStepper;
